import { c as _c } from "react/compiler-runtime";
import { usePageView } from '@/analytics/pageTypes';
import { SeoHeader } from '@/components/SeoHeader';
import { StackCards } from '@/components/StackCards';
import { AppstoreButton, Button, GooglePlayButton } from '@/components/buttons';
import AppIntro from '@/sections/homepage/AppIntro';
import { Blog } from '@/sections/homepage/Blog';
import { Testimonials } from '@/sections/Testimonials';
import { Layout } from '@/components/layout';
import Eshops from '@/sections/Eshops';
import MoreInfo from '@/sections/homepage/MoreInfo';
import Hero from '@/sections/Hero';
import { type IntlProps, getIntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import Image from 'next/image';
import { Trans, useTranslation } from 'react-i18next';
import { Discover } from '@/sections/homepage/Discover';
import React, { useRef } from 'react';
import { BlogPost, getBlogPosts } from '@/api/wordpress/blogPosts';
import { getPageBannerBySlug, PageBanner } from '@/api/sanity/pageBanners';
import { EXTERNAL_LINKS, LINKS } from '@/constants/links';
import { getTestimonialsBySlug, Testimonial } from '@/api/sanity/testimonials';
import { EshopsList, getAffiliateEshopsList } from '@/api/sanity/affiliateEshopsList';
import { AnimatedSection, AnimatedContent } from '@/components/animation';
import { useIsSelectedLanguage } from '@/hooks/useIsSelectedLanguage';
import { Link } from '@/components/links';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import { getSectionAnchorId } from '@/utils/anchors';
interface Props extends IntlProps {
  pageBanner: PageBanner | null;
  blogPosts: BlogPost[];
  eshops: EshopsList;
  testimonials: Testimonial[];
  menuPromoBanners?: PageMenuPromoBanner[];
}
export default function Home(props) {
  const $ = _c(138);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["core", "homepage"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  usePageView("home");
  const firstSectionRef = useRef(null);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (firstSectionRef.current) {
        firstSectionRef.current.scrollIntoView({
          behavior: "smooth"
        });
      }
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleScrollToFirstSection = t1;
  const {
    blogPosts,
    pageBanner,
    eshops,
    testimonials,
    menuPromoBanners
  } = props;
  const isCsLanguage = useIsSelectedLanguage("cs");
  let t2;
  if ($[2] !== t) {
    t2 = t("homepage:seo.title");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t) {
    t3 = t("homepage:seo.description");
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <SeoHeader title={t2} description={t3} />;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = pageBanner?.videoId;
  let t6;
  if ($[9] !== pageBanner?.title || $[10] !== t) {
    t6 = pageBanner?.title ?? t("homepage:heroSection.title");
    $[9] = pageBanner?.title;
    $[10] = t;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== pageBanner?.text || $[13] !== t) {
    t7 = pageBanner?.text ?? t("homepage:heroSection.paragraph");
    $[12] = pageBanner?.text;
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== pageBanner || $[16] !== t) {
    t8 = pageBanner ? pageBanner.buttons : [{
      ariaLabel: t("homepage:heroSection.buttonPrimary"),
      label: t("homepage:heroSection.buttonPrimary"),
      to: LINKS().registration,
      testId: "homepageHeroPrimaryCTA",
      analyticsParam: "homepageHeroPrimaryCTA",
      variant: "primary"
    }, {
      ariaLabel: t("homepage:heroSection.buttonSecondary"),
      label: t("homepage:heroSection.buttonSecondary"),
      onClick: handleScrollToFirstSection,
      testId: "homepageHeroSecondaryCTA",
      analyticsParam: "homepageHeroSecondaryCTA",
      variant: "secondary"
    }];
    $[15] = pageBanner;
    $[16] = t;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const t9 = pageBanner?.image?.url ?? `/images/sections/homepage/hero/${process.env.NEXT_PUBLIC_LANGUAGE}/hero.png`;
  let t10;
  if ($[18] !== pageBanner?.image?.alt || $[19] !== t) {
    t10 = pageBanner?.image?.alt ?? t("homepage:heroSection.imageAlt");
    $[18] = pageBanner?.image?.alt;
    $[19] = t;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t9) {
    t11 = {
      url: t9,
      alt: t10
    };
    $[21] = t10;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const t12 = pageBanner?.variant || "homepage";
  let t13;
  if ($[24] !== pageBanner || $[25] !== t) {
    t13 = pageBanner ? pageBanner.highlights : [{
      title: t("homepage:heroSection.extraBox.1.title"),
      text: t("homepage:heroSection.extraBox.1.text")
    }, {
      title: t("homepage:heroSection.extraBox.2.title"),
      text: t("homepage:heroSection.extraBox.2.text")
    }];
    $[24] = pageBanner;
    $[25] = t;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t11 || $[28] !== t12 || $[29] !== t13 || $[30] !== t5 || $[31] !== t6 || $[32] !== t7 || $[33] !== t8) {
    t14 = <Hero className="bg-blue-100" videoId={t5} title={t6} text={t7} buttons={t8} image={t11} variant={t12} highlights={t13} />;
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
    $[30] = t5;
    $[31] = t6;
    $[32] = t7;
    $[33] = t8;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = getSectionAnchorId("discover");
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <AnimatedSection bgColor="bg-blue-900" id={t15}><div ref={firstSectionRef}><AnimatedContent><Discover /></AnimatedContent></div></AnimatedSection>;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = getSectionAnchorId("eshops");
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== eshops) {
    t18 = eshops.length > 0 && <Eshops eshops={eshops} />;
    $[38] = eshops;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t18) {
    t19 = <AnimatedSection bgColor="bg-blue-50" id={t17}><AnimatedContent><div>{t18}</div></AnimatedContent></AnimatedSection>;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = getSectionAnchorId("story");
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== isCsLanguage) {
    t21 = isCsLanguage && <AnimatedContent className="relative z-10 pb-14 lg:pb-20"><AppIntro /></AnimatedContent>;
    $[43] = isCsLanguage;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== blogPosts || $[46] !== isCsLanguage) {
    t22 = isCsLanguage && <AnimatedContent><Blog posts={blogPosts} /></AnimatedContent>;
    $[45] = blogPosts;
    $[46] = isCsLanguage;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== testimonials) {
    t23 = <AnimatedContent><Testimonials testimonials={testimonials} /></AnimatedContent>;
    $[48] = testimonials;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t22 || $[51] !== t23) {
    t24 = <div className="relative z-10 flex flex-col gap-20 lg:gap-40">{t22}{t23}</div>;
    $[50] = t22;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <div className="bgCircle absolute top-2/3 right-1/2 -translate-x-[100%] translate-y-1/4 rotate-180" />;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== t21 || $[55] !== t24) {
    t26 = <AnimatedSection bgColor="bg-blue-100" id={t20}>{t21}{t24}{t25}</AnimatedSection>;
    $[54] = t21;
    $[55] = t24;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = getSectionAnchorId("cards");
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] !== isCsLanguage || $[59] !== t) {
    t28 = isCsLanguage && {
      menuTitle: t("homepage:card.menu.title.1"),
      title: <Trans t={t} i18nKey="homepage:card.title.1"><span className="text-blue-500" /></Trans>,
      description: <div>{t("homepage:card.description.1")}</div>,
      footer: <Button analyticsParam="homepageCardsCard" testId="homepageCardsCard" to={LINKS().card} variant="primary" className="w-full md:w-70 lg:w-60">{t("homepage:card.button.1")}</Button>,
      images: {
        desktop: {
          src: `/images/sections/homepage/cards/${process.env.NEXT_PUBLIC_LANGUAGE}/desktop/1.png`,
          alt: t("homepage:card.image.alt.1")
        },
        mobile: {
          src: `/images/sections/homepage/cards/${process.env.NEXT_PUBLIC_LANGUAGE}/mobile/1.png`,
          alt: t("homepage:card.image.alt.1")
        }
      }
    };
    $[58] = isCsLanguage;
    $[59] = t;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] !== t) {
    t29 = t("homepage:card.menu.title.2");
    $[61] = t;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  let t30;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <span className="text-blue-500" />;
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  let t31;
  let t32;
  if ($[64] !== t) {
    t31 = <Trans t={t} i18nKey="homepage:card.title.2">{t30}</Trans>;
    t32 = t("homepage:card.description.2");
    $[64] = t;
    $[65] = t31;
    $[66] = t32;
  } else {
    t31 = $[65];
    t32 = $[66];
  }
  let t33;
  if ($[67] !== t32) {
    t33 = <div>{t32}</div>;
    $[67] = t32;
    $[68] = t33;
  } else {
    t33 = $[68];
  }
  let t34;
  if ($[69] !== isCsLanguage || $[70] !== t) {
    t34 = isCsLanguage ? <Button analyticsParam="homepageCardsCsob" testId="homepageCardsCsob" to={EXTERNAL_LINKS().csobGroup} target="_blank" variant="primary" className="w-full md:w-70 lg:w-60">{t("homepage:card.button.2")}</Button> : <Button analyticsParam="homepageAboutCompanty" testId="homepageAboutCompanty" to={LINKS().aboutCompany} variant="primary" className="w-full md:w-70 lg:w-60">{t("homepage:card.button.2")}</Button>;
    $[69] = isCsLanguage;
    $[70] = t;
    $[71] = t34;
  } else {
    t34 = $[71];
  }
  let t35;
  if ($[72] !== t) {
    t35 = t("homepage:card.image.alt.2");
    $[72] = t;
    $[73] = t35;
  } else {
    t35 = $[73];
  }
  let t36;
  if ($[74] !== t35) {
    t36 = {
      src: `/images/sections/homepage/cards/${process.env.NEXT_PUBLIC_LANGUAGE}/desktop/2.png`,
      alt: t35
    };
    $[74] = t35;
    $[75] = t36;
  } else {
    t36 = $[75];
  }
  let t37;
  if ($[76] !== t) {
    t37 = t("homepage:card.image.alt.2");
    $[76] = t;
    $[77] = t37;
  } else {
    t37 = $[77];
  }
  let t38;
  if ($[78] !== t37) {
    t38 = {
      src: `/images/sections/homepage/cards/${process.env.NEXT_PUBLIC_LANGUAGE}/mobile/2.png`,
      alt: t37
    };
    $[78] = t37;
    $[79] = t38;
  } else {
    t38 = $[79];
  }
  let t39;
  if ($[80] !== t36 || $[81] !== t38) {
    t39 = {
      desktop: t36,
      mobile: t38
    };
    $[80] = t36;
    $[81] = t38;
    $[82] = t39;
  } else {
    t39 = $[82];
  }
  let t40;
  if ($[83] !== t29 || $[84] !== t31 || $[85] !== t33 || $[86] !== t34 || $[87] !== t39) {
    t40 = {
      menuTitle: t29,
      title: t31,
      description: t33,
      footer: t34,
      images: t39
    };
    $[83] = t29;
    $[84] = t31;
    $[85] = t33;
    $[86] = t34;
    $[87] = t39;
    $[88] = t40;
  } else {
    t40 = $[88];
  }
  let t41;
  if ($[89] !== t) {
    t41 = t("homepage:card.menu.title.3");
    $[89] = t;
    $[90] = t41;
  } else {
    t41 = $[90];
  }
  let t42;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <span className="text-blue-500" />;
    $[91] = t42;
  } else {
    t42 = $[91];
  }
  let t43;
  if ($[92] !== t) {
    t43 = <Trans t={t} i18nKey="homepage:card.title.3">{t42}</Trans>;
    $[92] = t;
    $[93] = t43;
  } else {
    t43 = $[93];
  }
  let t44;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = {
      mobapp_link: <Link key="mobappLink" analyticsParam="heroSectionMobileAppLink" testId="heroSectionMobileAppLink" to={LINKS().mobileApp} />
    };
    $[94] = t44;
  } else {
    t44 = $[94];
  }
  let t45;
  if ($[95] !== t) {
    t45 = <Trans t={t} i18nKey="homepage:card.description.3" components={t44} />;
    $[95] = t;
    $[96] = t45;
  } else {
    t45 = $[96];
  }
  let t46;
  if ($[97] !== t) {
    t46 = t("homepage:card.qr.code.alt");
    $[97] = t;
    $[98] = t46;
  } else {
    t46 = $[98];
  }
  let t47;
  if ($[99] !== t46) {
    t47 = <div className="hidden md:flex"><Image src={`/images/mobileApp/${process.env.NEXT_PUBLIC_LANGUAGE}/qr-code.png`} width={120} height={120} alt={t46} /></div>;
    $[99] = t46;
    $[100] = t47;
  } else {
    t47 = $[100];
  }
  let t48;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <div className="flex gap-1 md:flex-col"><AppstoreButton /><GooglePlayButton /></div>;
    $[101] = t48;
  } else {
    t48 = $[101];
  }
  let t49;
  if ($[102] !== t47) {
    t49 = <div className="flex items-center gap-8">{t47}{t48}</div>;
    $[102] = t47;
    $[103] = t49;
  } else {
    t49 = $[103];
  }
  let t50;
  if ($[104] !== t) {
    t50 = t("homepage:card.image.alt.3");
    $[104] = t;
    $[105] = t50;
  } else {
    t50 = $[105];
  }
  let t51;
  if ($[106] !== t50) {
    t51 = {
      src: `/images/sections/homepage/cards/${process.env.NEXT_PUBLIC_LANGUAGE}/desktop/3.png`,
      alt: t50
    };
    $[106] = t50;
    $[107] = t51;
  } else {
    t51 = $[107];
  }
  let t52;
  if ($[108] !== t) {
    t52 = t("homepage:card.image.alt.3");
    $[108] = t;
    $[109] = t52;
  } else {
    t52 = $[109];
  }
  let t53;
  if ($[110] !== t52) {
    t53 = {
      src: `/images/sections/homepage/cards/${process.env.NEXT_PUBLIC_LANGUAGE}/mobile/3.png`,
      alt: t52
    };
    $[110] = t52;
    $[111] = t53;
  } else {
    t53 = $[111];
  }
  let t54;
  if ($[112] !== t51 || $[113] !== t53) {
    t54 = {
      desktop: t51,
      mobile: t53
    };
    $[112] = t51;
    $[113] = t53;
    $[114] = t54;
  } else {
    t54 = $[114];
  }
  let t55;
  if ($[115] !== t41 || $[116] !== t43 || $[117] !== t45 || $[118] !== t49 || $[119] !== t54) {
    t55 = {
      menuTitle: t41,
      title: t43,
      description: t45,
      footer: t49,
      images: t54
    };
    $[115] = t41;
    $[116] = t43;
    $[117] = t45;
    $[118] = t49;
    $[119] = t54;
    $[120] = t55;
  } else {
    t55 = $[120];
  }
  let t56;
  if ($[121] !== t28 || $[122] !== t40 || $[123] !== t55) {
    t56 = [t28, t40, t55].filter(Boolean);
    $[121] = t28;
    $[122] = t40;
    $[123] = t55;
    $[124] = t56;
  } else {
    t56 = $[124];
  }
  let t57;
  if ($[125] !== isCsLanguage || $[126] !== t56) {
    t57 = <AnimatedContent className="relative bg-blue-50 pb-10 lg:pb-20"><StackCards imagePosition="right" showTab={isCsLanguage} cards={t56} /></AnimatedContent>;
    $[125] = isCsLanguage;
    $[126] = t56;
    $[127] = t57;
  } else {
    t57 = $[127];
  }
  let t58;
  if ($[128] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <MoreInfo />;
    $[128] = t58;
  } else {
    t58 = $[128];
  }
  let t59;
  if ($[129] !== t57) {
    t59 = <AnimatedSection bgColor="bg-blue-50" className="pb-0!" id={t27}>{t57}{t58}</AnimatedSection>;
    $[129] = t57;
    $[130] = t59;
  } else {
    t59 = $[130];
  }
  let t60;
  if ($[131] !== menuPromoBanners || $[132] !== t14 || $[133] !== t19 || $[134] !== t26 || $[135] !== t4 || $[136] !== t59) {
    t60 = <Layout menuPromoBanners={menuPromoBanners}>{t4}{t14}{t16}{t19}{t26}{t59}</Layout>;
    $[131] = menuPromoBanners;
    $[132] = t14;
    $[133] = t19;
    $[134] = t26;
    $[135] = t4;
    $[136] = t59;
    $[137] = t60;
  } else {
    t60 = $[137];
  }
  return t60;
}
export const getStaticProps = (async ctx => {
  return {
    props: {
      pageBanner: await getPageBannerBySlug('homepage'),
      testimonials: await getTestimonialsBySlug('homepage'),
      blogPosts: await getBlogPosts(),
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      eshops: await getAffiliateEshopsList('homepage'),
      ...(await getIntlProps(ctx, ['homepage', 'eshops', 'testimonials']))
    }
  };
}) satisfies GetStaticProps<Props>;